import { useMemo, type ReactNode } from 'react';
import ReactECharts from 'echarts-for-react';
import { Card } from 'react-bootstrap';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import { useAppContext } from '@/Main';
import { getColor } from '@/helpers/utils';
import { PANEL_FONT_FAMILY } from '../../../utils/panel-font';
import { chartChrome, escapeHtml, themePrimary } from '../../../utils/competition-chart';
import type {
  ProjectVisibilityBrandComparison,
  ProjectVisibilityTopBrand,
  ProjectVisibilityTopicBreakdown,
} from '../../../types';
import { padComparisonBrands } from '../../../utils/dashboard-competitors';
import { DashboardEmptyNote } from '../DashboardEmptyNote';

function limitTopicBrands(
  brands: ProjectVisibilityBrandComparison[],
  allowedKeys: Set<string> | null,
  entered?: ProjectVisibilityTopBrand[],
): ProjectVisibilityBrandComparison[] {
  const limited = allowedKeys
    ? brands.filter(
        (brand) =>
          brand.relationship === 'you' ||
          allowedKeys.has(brand.knownBrandId ?? brand.canonicalName),
      )
    : brands;
  return entered && !allowedKeys ? padComparisonBrands(limited, entered) : limited;
}

function brandIsFocus(
  brand: { knownBrandId: string | null; canonicalName: string; relationship: string },
  focusKey?: string | null,
): boolean {
  if (focusKey) return (brand.knownBrandId ?? brand.canonicalName) === focusKey;
  return brand.relationship === 'you';
}

export function TopicShareHeatmapCard({
  topics,
  topicNames,
  footer,
  previewKeys,
  hideHeader,
  enteredBrands,
  focusKey,
}: {
  topics: ProjectVisibilityTopicBreakdown[];
  topicNames: Map<string, string>;
  footer?: ReactNode;
  previewKeys?: Set<string> | null;
  hideHeader?: boolean;
  enteredBrands?: ProjectVisibilityTopBrand[];
  /** When set, this brand is bold instead of the own brand (domain filter). */
  focusKey?: string | null;
}) {
  const {
    config: { isDark, themeVariant },
  } = useAppContext() as { config: { isDark: boolean; themeVariant?: string } };
  const heatHue = themePrimary();

  const option = useMemo(() => {
    const brands = limitTopicBrands(
      topics[0]?.brands ?? [],
      previewKeys ?? null,
      enteredBrands,
    );
    if (!topics.length || !brands.length) return null;
    const chrome = chartChrome();
    const columns = topics.map((topic) => topicNames.get(topic.topicId) ?? 'بدون نام');
    // Category y-axis draws bottom-up; reverse so the own brand sits on top.
    const rows = [...brands].reverse();
    const cells = topics.flatMap((topic, x) =>
      rows.map((row, y) => {
        const brand = limitTopicBrands(topic.brands, previewKeys ?? null, enteredBrands).find(
          (item) =>
            item.relationship === row.relationship && item.canonicalName === row.canonicalName,
        );
        return [x, y, brand?.shareOfVoice ?? 0, brand?.aiMentions ?? 0];
      }),
    );
    const maxShare = Math.max(10, ...cells.map((cell) => cell[2]));
    const data = cells.map((cell) => ({
      value: cell,
      // Past ~55% of the ramp the cell is dark enough to need light text.
      label: { color: cell[2] / maxShare > 0.55 ? '#ffffff' : chrome.strongText },
    }));

    return {
      tooltip: {
        backgroundColor: chrome.tooltipBg,
        borderColor: chrome.tooltipBorder,
        borderWidth: 1,
        extraCssText: 'direction: rtl; text-align: right;',
        textStyle: { color: chrome.tooltipText, fontSize: 12, fontFamily: PANEL_FONT_FAMILY },
        formatter: (param: { value: [number, number, number, number] }) => {
          const [x, y, share, mentions] = param.value;
          const brand = rows[y];
          return `<div style="font-family:${PANEL_FONT_FAMILY};font-size:12px;direction:rtl;text-align:right;color:${chrome.tooltipText}">
            <div style="font-weight:600">${escapeHtml(brand.canonicalName)}${brand.relationship === 'you' && !focusKey ? ' (شما)' : ''}</div>
            <div style="color:${chrome.muted}">${escapeHtml(columns[x])} · ${topics[x].responses} پاسخ</div>
            <div style="margin-top:4px">سهم از دیده‌شدن: <strong>${share}%</strong> · منشن ${mentions}</div></div>`;
        },
      },
      grid: { left: 108, right: 8, top: 36, bottom: 8, containLabel: false },
      xAxis: {
        type: 'category',
        data: columns,
        position: 'top',
        splitArea: { show: false },
        axisTick: { show: false },
        axisLine: { show: false },
        axisLabel: {
          color: chrome.axisLabel,
          fontSize: 11,
          fontFamily: PANEL_FONT_FAMILY,
          interval: 0,
          hideOverlap: false,
          overflow: 'truncate',
          width: 80,
        },
      },
      yAxis: {
        type: 'category',
        position: 'left',
        data: rows.map((row) =>
          brandIsFocus(row, focusKey) ? `{own|${row.canonicalName}}` : row.canonicalName,
        ),
        axisTick: { show: false },
        axisLine: { show: false },
        axisLabel: {
          show: true,
          interval: 0,
          hideOverlap: false,
          align: 'left',
          margin: 96,
          color: chrome.strongText,
          fontSize: 11,
          fontFamily: PANEL_FONT_FAMILY,
          overflow: 'truncate',
          width: 96,
          rich: { own: { color: chrome.strongText, fontWeight: 'bold', fontSize: 11 } },
        },
      },
      visualMap: {
        show: false,
        min: 0,
        max: maxShare,
        dimension: 2,
        inRange: { color: [getColor('gray-200'), heatHue] },
      },
      series: [
        {
          type: 'heatmap',
          data,
          itemStyle: { borderColor: chrome.surface, borderWidth: 2, borderRadius: 4 },
          label: {
            show: true,
            fontSize: 11,
            fontFamily: PANEL_FONT_FAMILY,
            formatter: (param: { value: [number, number, number] }) => `${param.value[2]}%`,
          },
          emphasis: { itemStyle: { borderColor: chrome.strongText, borderWidth: 1 } },
        },
      ],
    };
  }, [topics, topicNames, isDark, heatHue, previewKeys, enteredBrands, focusKey]);

  const brandCount = limitTopicBrands(
    topics[0]?.brands ?? [],
    previewKeys ?? null,
    enteredBrands,
  ).length;

  return (
    <Card className="h-100 shadow-sm d-flex flex-column">
      {hideHeader ? null : (
        <FalconCardHeader
          light
          titleTag="h6"
          className="py-3"
          title={
            <div>
              سهم از دیده‌شدن به تفکیک موضوع
              <p className="fs--2 text-700 mb-0 fw-normal mt-2">
                در هر موضوع، سهم هر برند از منشن‌های همه برندها
              </p>
            </div>
          }
        />
      )}
      <Card.Body className="pt-2 flex-grow-1">
        {!option ? (
          <DashboardEmptyNote>در این بازه پاسخی برای موضوع‌ها ثبت نشده.</DashboardEmptyNote>
        ) : (
          <>
            <div dir="ltr" style={{ overflowX: 'auto' }}>
              <ReactECharts
                key={`${isDark ? 'dark' : 'light'}-${themeVariant ?? 'primary'}`}
                option={option}
                notMerge
                style={{
                  height: brandCount * 40 + 56,
                  minWidth: Math.max(320, topics.length * 90 + 140),
                }}
                opts={{ renderer: 'svg' }}
              />
            </div>
            <div className="mt-2 d-flex align-items-center justify-content-center gap-2 fs--2 text-700">
              <span>۰٪</span>
              <span
                className="rounded-pill"
                style={{
                  width: '6rem',
                  height: '0.5rem',
                  background: `linear-gradient(to left, var(--falcon-gray-200), ${heatHue})`,
                }}
              />
              <span>بیشترین سهم</span>
            </div>
          </>
        )}
      </Card.Body>
      {footer}
    </Card>
  );
}
